/**
 * iter-312q: Apply Payments to BOQ.
 *
 * Modal that lists all paid client payments for a project, shows their available
 * balance (= amount − sum already applied to OTHER BOQs of the same project),
 * and lets the admin pick which payments to apply to THIS BOQ — with optional
 * partial-split amounts per payment.
 *
 * On save, the parent gets a list of `{payment_id, applied_amount}` to persist
 * onto the BOQ worksheet via the existing PUT endpoint.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Banknote, Wallet, Info } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ApplyPaymentsDialog({ open, onOpenChange, projectId, worksheetId, initialApplied = [], onApply }) {
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  // selected[paymentId] = applied_amount (number); presence = checkbox on.
  const [selected, setSelected] = useState({});

  // Hydrate from initialApplied whenever the modal opens or BOQ data refreshes.
  useEffect(() => {
    const map = {};
    (initialApplied || []).forEach(ap => { if (ap?.payment_id) map[ap.payment_id] = +ap.applied_amount || 0; });
    setSelected(map);
  }, [initialApplied, open]);

  const reload = useCallback(async () => {
    if (!projectId || !open) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/applicable-payments`, {
        params: { exclude_boq_id: worksheetId },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setAvailable(r.data.payments || []);
    } catch {
      toast.error('Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [projectId, worksheetId, open]);

  useEffect(() => { reload(); }, [reload]);

  const toggle = (p) => {
    setSelected(prev => {
      const next = { ...prev };
      if (p.id in next) delete next[p.id];
      else {
        // Default to (available_balance + amount already applied here from initial) clamped to the payment's amount.
        const initialHere = (initialApplied || []).find(x => x.payment_id === p.id);
        next[p.id] = +(initialHere?.applied_amount ?? Math.max(0, p.available_balance));
      }
      return next;
    });
  };

  const setAmount = (p, v) => {
    const num = v === '' ? 0 : parseFloat(v) || 0;
    // Hard cap: cannot exceed (available_balance + amount already applied here).
    const initialHere = (initialApplied || []).find(x => x.payment_id === p.id);
    const ceiling = p.available_balance + (+initialHere?.applied_amount || 0);
    const clamped = Math.min(Math.max(0, num), ceiling);
    setSelected(prev => ({ ...prev, [p.id]: clamped }));
  };

  const total = useMemo(() => Object.values(selected).reduce((s, v) => s + (+v || 0), 0), [selected]);

  const handleApply = () => {
    const list = Object.entries(selected)
      .filter(([, amt]) => +amt > 0)
      .map(([payment_id, amt]) => ({ payment_id, applied_amount: +amt }));
    onApply && onApply(list);
    onOpenChange(false);
  };

  const handleClearAll = () => setSelected({});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl !max-h-[88vh] flex flex-col overflow-hidden p-0"
        style={{ zoom: 1.25 }}
        data-testid="apply-payments-dialog"
      >
        {/* Sticky header */}
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-stone-200 bg-white flex-shrink-0">
          <DialogTitle>Apply Client Payments to this BOQ</DialogTitle>
        </DialogHeader>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-3 space-y-3 min-h-0">
          <div className="rounded-md border border-blue-200 bg-blue-50/60 px-3 py-2 text-xs text-blue-800 flex items-start gap-2">
            <Info size={14} className="mt-0.5 flex-shrink-0" />
            <span>
              Tick a payment to apply its <strong>available balance</strong> to this BOQ. Adjust the amount per payment for partial splits.
              Available balance = payment amount − amounts already applied to <em>other</em> BOQs of this project.
            </span>
          </div>

          <div className="rounded-md border border-stone-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-stone-100 text-stone-600 text-[11px] uppercase tracking-wider sticky top-0 z-[1]">
                <tr>
                  <th className="px-2 py-2 w-8 text-center"><span className="sr-only">Select</span></th>
                  <th className="px-2 py-2 text-left">Date</th>
                  <th className="px-2 py-2 text-left">Payment #</th>
                  <th className="px-2 py-2 text-left">Mode</th>
                  <th className="px-2 py-2 text-left">Notes</th>
                  <th className="px-2 py-2 text-right">Amount</th>
                  <th className="px-2 py-2 text-right">Available</th>
                  <th className="px-2 py-2 text-right w-36">Apply (₹)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {loading && (
                  <tr><td colSpan={8} className="p-6 text-center text-stone-400">Loading payments…</td></tr>
                )}
                {!loading && available.length === 0 && (
                  <tr><td colSpan={8} className="p-6 text-center text-stone-400">No paid client payments for this project yet.</td></tr>
                )}
                {!loading && available.map((p) => {
                  const checked = p.id in selected;
                  return (
                    <tr key={p.id} className={`${checked ? 'bg-emerald-50/40' : 'hover:bg-stone-50'} transition-colors`} data-testid={`apply-row-${p.id}`}>
                      <td className="px-2 py-2 text-center">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggle(p)}
                          className="w-4 h-4 accent-emerald-600"
                          data-testid={`apply-checkbox-${p.id}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-stone-700 whitespace-nowrap">{p.date || '-'}</td>
                      <td className="px-2 py-2">
                        <span className={`inline-flex items-center gap-1 font-mono text-xs px-1.5 py-0.5 rounded ${p.is_advance ? 'bg-amber-100 text-amber-800 border border-amber-200' : 'bg-stone-100 text-stone-700'}`}>
                          {p.is_advance ? <Wallet size={11} /> : <Banknote size={11} />}
                          {p.payment_number}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-stone-600 capitalize text-xs">{(p.payment_mode || '').replace('_', ' ')}</td>
                      <td className="px-2 py-2 text-stone-500 text-xs max-w-[260px] truncate" title={p.notes}>{p.notes || '-'}</td>
                      <td className="px-2 py-2 text-right font-medium text-stone-800">₹{p.amount.toLocaleString()}</td>
                      <td className="px-2 py-2 text-right text-emerald-700">₹{p.available_balance.toLocaleString()}</td>
                      <td className="px-2 py-2 text-right">
                        {checked ? (
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={selected[p.id]}
                            onChange={(e) => setAmount(p, e.target.value)}
                            className="h-7 text-right text-sm"
                            data-testid={`apply-amount-${p.id}`}
                          />
                        ) : (
                          <span className="text-stone-300 text-xs">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Sticky footer */}
        <div className="px-6 py-3 border-t border-stone-200 bg-white flex-shrink-0 flex items-center justify-between gap-2 flex-wrap">
          <div className="text-sm text-stone-600">
            {Object.keys(selected).length > 0 && (
              <>
                <span className="font-semibold text-emerald-700">{Object.keys(selected).length}</span> selected ·
                Total to apply: <strong className="text-emerald-700">₹{Math.round(total).toLocaleString()}</strong>
              </>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={handleClearAll} data-testid="apply-clear-all">Clear all</Button>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleApply} className="bg-emerald-600 hover:bg-emerald-700" data-testid="apply-save-btn">
              Apply {Math.round(total) > 0 ? `(₹${Math.round(total).toLocaleString()})` : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
